/**
 * User utility functions
 */

/**
 * Check if user is admin or super admin
 * @param {Object} user - User object with a `type` property
 * @returns {boolean} True if user has admin privileges
 */
export function isAdminUser(user) {
  if (!user || !user.type) {
    return false;
  }
  return user.type === 'admin' || user.type === 'super';
}

/**
 * Get initials from a name
 * @param {string} name - The user's name
 * @returns {string} The initials (max 2 characters)
 */
export const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Generate a consistent color based on email
 * @param {string} email - The user's email
 * @returns {string} Hex color code
 */
export const getUserColor = (email) => {
  if (!email) return '#6b7280';

  const colors = [
    '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
    '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
    '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
    '#ec4899', '#f43f5e'
  ];

  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

/**
 * Find a user in a list by id. Auth records key on `uid`, Firestore user docs
 * on `id`, and both shapes end up in the same lists — matching only one of them
 * silently returns undefined.
 * @param {Array} users
 * @param {string} userId
 * @returns {Object|null}
 */
export function findUserById(users, userId) {
  if (!userId || !Array.isArray(users)) return null;
  return users.find((u) => u?.id === userId || u?.uid === userId) || null;
}

/**
 * Best available display name. Auth records carry `displayName`, Firestore user
 * docs carry `name`; falling back to email beats rendering nothing.
 * @param {Object} user
 * @param {string} [fallback]
 * @returns {string}
 */
export function userDisplayName(user, fallback = '') {
  if (!user) return fallback;
  return user.displayName || user.name || user.email || fallback;
}
